import { supabase } from './supabaseClient';
import type { MetronomeSettings, PlaybackMode, SongMarker, SongRecord } from './types';

export interface SyncedSongSummary {
  id: string;
  name: string;
  markerCount: number;
}

export interface CloudSongRecord {
  id: string;
  bandId: string;
  name: string;
  mode: PlaybackMode;
  originalFileName: string | null;
  fileFormat: SongRecord['fileFormat'];
  durationSeconds: number | null;
  channelCount: number | null;
  fileSizeBytes: number | null;
  metronomeSettings: MetronomeSettings;
  syntheticClickEnabled: boolean;
  syntheticClickOffsetSeconds: number;
  firstBeatTimeSeconds: number | null;
  beatOneReferenceSeconds: number | null;
  markerLeadInSeconds: number;
  markerLeadInBeats: number | null;
  markers: SongMarker[];
}

/**
 * Pushes a local song's canonical fields + markers to Supabase under the
 * given band — creating it on first sync (storing the returned id back
 * onto the local SongRecord as supabaseSongId), or updating that same row
 * on every sync after.
 *
 * Marker identity is deliberately stable. `markers.client_id` stores the
 * local SongMarker.id, so subsequent syncs update the existing Supabase row
 * instead of deleting/recreating it. This matters because owner-private
 * `personal_marker_notes` rows reference the Supabase marker UUID; replacing
 * marker rows would cascade-delete those notes on every host re-sync.
 */
export async function syncSongToBand(song: SongRecord, bandId: string): Promise<{ supabaseSongId: string | null; error: string | null }> {
  if (!supabase) return { supabaseSongId: null, error: 'Supabase is not configured in this build.' };

  const songPayload = {
    band_id: bandId,
    name: song.name,
    mode: song.mode,
    original_file_name: song.originalFileName,
    file_format: song.fileFormat,
    duration_seconds: song.durationSeconds,
    channel_count: song.channelCount,
    file_size_bytes: song.fileSizeBytes,
    metronome_settings: song.metronomeSettings,
    synthetic_click_enabled: song.syntheticClickEnabled,
    synthetic_click_offset_seconds: song.syntheticClickOffsetSeconds,
    first_beat_time_seconds: song.firstBeatTimeSeconds,
    beat_one_reference_seconds: song.beatOneReferenceSeconds,
    marker_lead_in_seconds: song.markerLeadInSeconds,
    marker_lead_in_beats: song.markerLeadInBeats,
  };

  let supabaseSongId = song.supabaseSongId;

  if (supabaseSongId) {
    const { error } = await supabase.from('songs').update(songPayload).eq('id', supabaseSongId);
    if (error) return { supabaseSongId: null, error: error.message };
  } else {
    const { data, error } = await supabase.from('songs').insert(songPayload).select('id').single();
    if (error) return { supabaseSongId: null, error: error.message };
    supabaseSongId = data.id as string;
  }

  // Fetch the current cloud marker mapping before changing anything. Rows
  // created by the pre-stable-identity implementation have client_id=null;
  // they cannot be matched safely and are removed only after all current
  // local markers have been upserted.
  const { data: existingRows, error: existingError } = await supabase
    .from('markers')
    .select('id, client_id')
    .eq('song_id', supabaseSongId);
  if (existingError) return { supabaseSongId, error: existingError.message };

  if (song.markers.length > 0) {
    const markerRows = song.markers.map((m) => ({
      song_id: supabaseSongId,
      client_id: m.id,
      label: m.label,
      short_label: m.shortLabel,
      time_seconds: m.timeSeconds,
    }));
    const { error: upsertError } = await supabase
      .from('markers')
      .upsert(markerRows, { onConflict: 'song_id,client_id' });
    if (upsertError) return { supabaseSongId, error: upsertError.message };
  }

  const liveClientIds = new Set(song.markers.map((m) => m.id));
  const staleIds = (existingRows ?? [])
    .filter((row) => typeof row.client_id !== 'string' || !liveClientIds.has(row.client_id))
    .map((row) => row.id as string);

  // Delete only markers that genuinely disappeared locally (plus legacy
  // null-client-id rows from before this migration). Existing matched rows
  // keep their UUIDs, preserving every personal note foreign key.
  if (staleIds.length > 0) {
    const { error: deleteError } = await supabase.from('markers').delete().in('id', staleIds);
    if (deleteError) return { supabaseSongId, error: deleteError.message };
  }

  return { supabaseSongId, error: null };
}

/** Read-only summary of a band's synced songs — not yet wired into
 * Library/Setlists/Performance at all, purely informational (see the
 * "My Bands" card in Settings → Account). */
export async function listBandSongs(bandId: string): Promise<{ songs: SyncedSongSummary[]; error: string | null }> {
  if (!supabase) return { songs: [], error: 'Supabase is not configured in this build.' };
  const { data, error } = await supabase.from('songs').select('id, name, markers(count)').eq('band_id', bandId);
  if (error) return { songs: [], error: error.message };
  const songs: SyncedSongSummary[] = (data ?? []).map((row) => {
    const markerCountRows = row.markers as unknown as { count: number }[] | null;
    return { id: row.id as string, name: row.name as string, markerCount: markerCountRows?.[0]?.count ?? 0 };
  });
  return { songs, error: null };
}

export async function listCloudBandSongs(bandId: string): Promise<{ songs: CloudSongRecord[]; error: string | null }> {
  if (!supabase) return { songs: [], error: 'Supabase is not configured in this build.' };
  const { data, error } = await supabase
    .from('songs')
    .select(`
      id,
      band_id,
      name,
      mode,
      original_file_name,
      file_format,
      duration_seconds,
      channel_count,
      file_size_bytes,
      metronome_settings,
      synthetic_click_enabled,
      synthetic_click_offset_seconds,
      first_beat_time_seconds,
      beat_one_reference_seconds,
      marker_lead_in_seconds,
      marker_lead_in_beats,
      markers(id, client_id, label, short_label, time_seconds)
    `)
    .eq('band_id', bandId);
  if (error) return { songs: [], error: error.message };

  const songs: CloudSongRecord[] = (data ?? []).map((row) => ({
    id: row.id as string,
    bandId: row.band_id as string,
    name: row.name as string,
    mode: row.mode as PlaybackMode,
    originalFileName: (row.original_file_name as string | null) ?? null,
    fileFormat: row.file_format === 'wav' || row.file_format === 'mp3' ? row.file_format : null,
    durationSeconds: (row.duration_seconds as number | null) ?? null,
    channelCount: (row.channel_count as number | null) ?? null,
    fileSizeBytes: (row.file_size_bytes as number | null) ?? null,
    metronomeSettings: row.metronome_settings as MetronomeSettings,
    syntheticClickEnabled: Boolean(row.synthetic_click_enabled),
    syntheticClickOffsetSeconds: Number(row.synthetic_click_offset_seconds ?? 0),
    firstBeatTimeSeconds: (row.first_beat_time_seconds as number | null) ?? null,
    beatOneReferenceSeconds: (row.beat_one_reference_seconds as number | null) ?? null,
    markerLeadInSeconds: Number(row.marker_lead_in_seconds ?? 3),
    markerLeadInBeats: (row.marker_lead_in_beats as number | null) ?? null,
    markers: ((row.markers as Array<{ client_id: string | null; label: string; short_label: string | null; time_seconds: number }> | null) ?? [])
      .filter((marker) => typeof marker.client_id === 'string')
      .map((marker) => ({
        id: marker.client_id!,
        label: marker.label,
        shortLabel: marker.short_label ?? '',
        timeSeconds: marker.time_seconds,
        notes: {},
        noteFormats: {},
        noteSheets: {},
      }))
      .sort((a, b) => a.timeSeconds - b.timeSeconds),
  }));
  return { songs, error: null };
}
